import { initSdk } from '../target/generated/sdk';
import { getAxiosInstance, setAxiosInstance } from './axios/axios.utilities';
import { resolveOrigin } from './axios/requestUrl.utilities';
import { factoryOverrides } from './factoryOverrides';
import { useAxiosInstance } from './reactQuery/ApiProvider.context';
import type { AxiosInstance } from 'axios';

/** Options for configuring the generated SDK runtime. */
type SetupApiSdkOptions = {
  /**
   * Prefix that every generated request url is built from. It may include an origin (`https://example.com/api`) or just
   * be a path on the page's own origin (`/api`). Its origin is also what request lifecycle events are reported against,
   * unless the axios instance has a `baseUrl` of its own.
   */
  baseUrl: string;
  /**
   * The axios instance every generated endpoint sends through, already carrying whatever interceptors the app needs. It
   * also becomes the package's shared instance, so `getAxiosInstance` and `<ApiProvider>` hand out this one.
   */
  axiosInstance: AxiosInstance;
  /**
   * Turns a request's query params into a query string. Defaults to `URLSearchParams`, which repeats the key for each
   * element of an array. Override it when the server expects a different array format.
   */
  serializeParams?: (params: Record<string, unknown>) => string;
};

let initialized = false;

/**
 * Initialize the generated SDK with base URL, hooks, and a configured axios instance.
 * Call once during app startup before using generated endpoint hooks.
 */
export function setupApiSdk({ baseUrl, axiosInstance, serializeParams }: SetupApiSdkOptions) {
  if (initialized) {
    throw new Error('API SDK has already been initialized. Multiple initializations are not supported.');
  }
  initialized = true;

  setAxiosInstance(axiosInstance);

  const origin = resolveOrigin(baseUrl);

  initSdk({
    baseUrl,
    origin,
    getAxiosInstance,
    useAxiosInstance,
    serializeParams,
    factoryOverrides,
  });
}
